import clsx from 'clsx';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Icon } from 'ui';

const AttentionCard = ({
  title,
  text1,
  text2,
  buttonText1,
  buttonText2,
  buttonDisabled1,
  buttonDisabled2,
  icon,
  isShowedButton1,
  isShowedButton2,
  iconSize,
  onClickButton1,
  onClickButton2,
  color,
  isAlert,
}) => {
  // `region` (section con nombre) en vez de `complementary` (<aside>): un <aside> dentro de <main>
  // dispara la regla axe landmark-complementary-is-top-level. El nombre lo aporta el encabezado
  // real vía aria-labelledby (única fuente del texto, sin duplicarlo en aria-label + <span>).
  const [titleId] = useState(() => uniqueId('attention-card-title-'));
  const sectionRef = useRef(null);

  // A11y (TAN-10011, WCAG 4.1.3/2.4.3): as a blocking alert, announce it (role="alert") and pull
  // focus to it so the state change is not silent for screen readers and focus does not fall to
  // <body>. Opt-in via `isAlert`; default keeps the neutral `region` semantics for other uses.
  useEffect(() => {
    if (isAlert && sectionRef.current && document.activeElement === document.body) {
      sectionRef.current.focus();
    }
  }, [isAlert]);

  return (
    <section
      ref={sectionRef}
      role={isAlert ? 'alert' : undefined}
      tabIndex={isAlert ? -1 : undefined}
      aria-labelledby={title ? titleId : undefined}
      className={clsx('attention-card', 'attention-card--' + color)}
    >
      <div className="attention-card__info">
        <div className="attention-card__icon">
          <Icon name={icon} color="primary" size={iconSize} />
        </div>
        <div className="attention-card__texts">
          <h2 className="attention-card__title" id={titleId}>
            {title}
          </h2>
          {text1 && <span className="attention-card__text">{text1}</span>}
          {text2 && <span className="attention-card__text">{text2}</span>}
        </div>
      </div>
      <div className="attention-card__actions">
        {isShowedButton1 && <Button text={buttonText1} isDisabled={buttonDisabled1} onClick={onClickButton1} isFullWidth />}
        {isShowedButton2 && <Button text={buttonText2} variant="warning" isDisabled={buttonDisabled2} onClick={onClickButton2} isFullWidth />}
      </div>
    </section>
  );
};

AttentionCard.propTypes = {
  title: PropTypes.string,
  text1: PropTypes.string,
  text2: PropTypes.string,
  buttonText1: PropTypes.string,
  buttonText2: PropTypes.string,
  buttonDisabled1: PropTypes.bool,
  buttonDisabled2: PropTypes.bool,
  isShowedButton1: PropTypes.bool,
  isShowedButton2: PropTypes.bool,
  onClickButton1: PropTypes.func,
  onClickButton2: PropTypes.func,
  icon: PropTypes.string,
  iconSize: PropTypes.oneOf(['xs', 'md', 'xl', 'xxl', '3xl', '4xl']),
  color: PropTypes.oneOf(['primary', 'secondary', 'transparent-primary', 'transparent-secondary', 'light', 'success', 'red', 'yellow']),
  isAlert: PropTypes.bool,
};

AttentionCard.defaultProps = {
  buttonDisabled1: false,
  buttonDisabled2: false,
  color: 'transparent-primary',
  isAlert: false,
};

export default AttentionCard;
